import React, { useEffect, useState } from 'react'

import { StackType } from 'types/deck'

import { useAppSelector } from 'redux/hooks'
import { selectStacks } from 'redux/deck/selectors'

import ImageStack from 'components/deckPage/DeckContainerV2/cardGroups/ImageStack'
import StickiedDeckControls from 'components/deckPage/filtersAndControls/StickiedDeckControls'

import { getColumnCount } from 'components/deckPage/DeckContainerV2/layouts/helperFunctions'

import { useDebouncedResizeObserver } from 'hooks/resizeObservers'

import styles from './stack.module.scss'

type Props = {
  stickiedControlsClassName?: string
}

const StackLayout = ({ stickiedControlsClassName }: Props) => {
  const imageSize = useAppSelector(state => state.cacheables.imageSize)
  const { mainDeck, sideDeck } = useAppSelector(selectStacks)

  const { ref, width = 1 } = useDebouncedResizeObserver<HTMLDivElement>(200)

  const [mounted, setMounted] = useState(false)

  const columns = getColumnCount(imageSize, width)
  const gridStyles: React.CSSProperties = {
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    opacity: mounted ? 1 : 0,
  }

  useEffect(() => {
    setTimeout(() => setMounted(true), 1000)
  }, [])

  // Used to create the faux masonry layout
  // NOTE - this can be replaced with masonry when it goes mainline
  const displayColumns: StackType[][] = Array.from(Array(columns).keys()).map(() => [])

  for (let i = 0; i < mainDeck.length; i++) {
    const stack = mainDeck[i]
    const allowedColumns = sideDeck.length && columns > 1 ? columns - 1 : columns

    const columnPlacement = i % allowedColumns

    displayColumns[columnPlacement].push(stack)
  }

  if (sideDeck.length) {
    if (columns > 1) displayColumns[columns - 1] = sideDeck
    else displayColumns[0].push(...sideDeck)
  }

  let maxWidthClass: string | undefined = undefined

  if (imageSize === 2 && columns <= 1) maxWidthClass = styles.normalMobileWidth
  if (imageSize === 3 && columns <= 1) maxWidthClass = styles.largeMobileWidth

  return (
    <div className={styles.container}>
      <StickiedDeckControls className={stickiedControlsClassName} />

      <div className={styles.columns} style={gridStyles} ref={ref}>
        {displayColumns.map((column, columnNumber) => (
          <div
            key={`column-${columnNumber}`}
            className={`
              ${styles.column}
              ${
                columnNumber === displayColumns.length - 1 && columns > 1 && sideDeck.length
                  ? styles.sidedeckColumn
                  : undefined
              }
            `}>
            {column.map((stack, placeInColumn) => (
              <ImageStack
                cardStackClassName={maxWidthClass}
                key={`placement-${placeInColumn}`}
                cardIds={stack.cardIds}
                name={stack.name}
                price={stack.price}
                quantity={stack.quantity}
                mdfcQuantity={stack.mdfcQuantity}
                isPremier={stack.isPremier}
                includedInDeck={stack.includedInDeck}
                includeInPrice={stack.includedInPrice}
              />
            ))}

            {!column.length && <div className={styles.emptyColumn}>Empty column</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

export default StackLayout
